import { Autocomplete, Grid } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import FreeTagList from "components/FreeTagList";
import MDEditor from "components/MDEditor";
import MDTypography from "components/MDTypography";
import { useAppContextController } from "context/AppContext";
import useDropdowns from "hooks/useDropdowns";
import FormField from "layouts/applications/wizard/components/FormField";
import generateAssessmentSkills from "layouts/pages/assessments/actions/generateAssessmentSkills";
import SelectHiringManager from "layouts/pages/events/components/InterviewEditor/SelectHiringManager";
import SelectInterviewLocation from "layouts/pages/events/components/InterviewEditor/SelectInterviewLocation";
import isEmpty from "lodash/isEmpty";
import { useSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import { Controller, useWatch } from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";
import { INTERVIEW_ENTITIES } from "utils/constants/entityOptions";

const useStyle = makeStyles({
  editor: {
    height: 150,
  },
  box: {
    overflow: "visible",
    height: 550,
  },
  error: {
    fontSize: "0.75rem",
  },
});

const { INTERVIEW_TYPES, STATES, INTERVIEW_STATUS } = INTERVIEW_ENTITIES;

const BasicInfoStep = ({
  clearErrors,
  setValue,
  control,
  getValues,
  errors,
  trigger,
  watch,
  setError,
}) => {
  const { company, currentLoggedUser } = useAppContextController();
  const queryClient = useQueryClient();
  const classes = useStyle();
  const [editorValue, setEditorValue] = useState(null);
  const interviewLocationListRef = useRef([]);
  const { enqueueSnackbar } = useSnackbar();
  const [interviewTypesList, setInterviewTypesList] = useState();

  const watchLocal = useWatch({
    control,
  });

  const { dropdowns: interviewTypes } = useDropdowns({ entity: INTERVIEW_TYPES });

  const { mutateAsync: generateAssessmentSkillsMutation, isLoading } = useMutation(
    generateAssessmentSkills,
    {
      onSuccess: async (res, vars) => {
        enqueueSnackbar("Skills generated successfully.", { variant: "success" });
        setValue("skills", res.skills, { shouldDirty: true });
      },
      onError: (err) => enqueueSnackbar(`An error occured: ${err}`, { variant: "error" }),
    }
  );

  const handleGenerateQuestions = () => {
    const div = document.createElement("div");
    div.innerHTML = watch("description") ?? "";
    const plainDescription = div.innerText;
    if (!plainDescription.length) {
      enqueueSnackbar("A description is required.", { variant: "error" });
      return;
    }
    generateAssessmentSkillsMutation({ description: watch("description"), hasName: true });
  };

  return (
    <>
      <Grid item xs={12} sm={6}>
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <FormField
              type="text"
              disabled
              label="Assessment Name"
              {...field}
              value={field.value || ""}
            />
          )}
        />
        {errors?.name && (
          <MDTypography className={classes.error} color="error">
            {errors?.name.message}
          </MDTypography>
        )}
      </Grid>

      <Grid item xs={12} sm={6}>
        <Controller
          name="type"
          control={control}
          render={({ field }) => (
            <Autocomplete
              options={["Multiple Choice", "Fill-In", "True/False", "Excel", "Hybrid"]}
              tabIndex="-1"
              name="type"
              value={field.value || ""}
              onChange={(e, v) => {
                field.onChange(e.target.textContent);
                setValue("questions", []);
              }}
              renderInput={(params) => <FormField {...params} type="text" label="Type" />}
            />
          )}
        />
        {errors?.type && (
          <MDTypography className={classes.error} color="error">
            {errors?.type.message}
          </MDTypography>
        )}
      </Grid>
      <Grid item xs={12} sx={{ height: "230px" }}>
        <Controller
          name="description"
          control={control}
          render={({ field: { value, onChange, ...props } }) => (
            <MDEditor
              className={classes.editor}
              theme="snow"
              onChange={(text, delta, source, editor) => {
                if (source === "user") onChange(text);
              }}
              value={value || ""}
              placeholder="Write Content"
              {...props}
            />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          name="skills"
          control={control}
          defaultValue={watch("skills")}
          render={({ field }) => (
            <FreeTagList
              {...field}
              setValue={setValue}
              setError={setError}
              clearErrors={clearErrors}
              currentComponent={watchLocal}
              label="Skills Measured"
              placeholder="Type a skill and press Enter"
              onGenerateSkills={handleGenerateQuestions}
              isLoadingGenerateSkills={isLoading}
            />
          )}
        />
        {errors?.invalidTag && (
          <MDTypography color="error">{errors?.invalidTag?.message}</MDTypography>
        )}
      </Grid>
    </>
  );
};

export default BasicInfoStep;
